import { collectSections, SECTION_TITLES } from "../diag/collect";
import { diagMark } from "../diag/capture";
import { copyText } from "../diag/clipboard";
import { formatReport, type Section } from "../diag/format";

/**
 * Full-screen sheet with the Diagnostics report: what the running app, its server, the service
 * worker, storage, the open walk and the device report, as plain text the owner copies and
 * pastes to the developer. Nothing runs while it is closed (no timers, no DOM); opening it
 * collects once, Refresh collects again.
 */
export class DiagnosticsPanel {
  private root: HTMLElement | null = null;
  private body: HTMLElement | null = null;
  private status: HTMLElement | null = null;
  private sections: (Section | null)[] = [];
  private run = 0;
  private lastFocus: Element | null = null;

  get isOpen(): boolean {
    return this.root !== null;
  }

  open(): void {
    if (this.root) return;
    this.lastFocus = document.activeElement;
    const root = document.createElement("div");
    root.className = "diag";
    root.setAttribute("role", "dialog");
    root.setAttribute("aria-modal", "true");
    root.setAttribute("aria-label", "Diagnostics");
    root.innerHTML = `
      <div class="diag-bar">
        <h2>Diagnostics</h2>
        <button type="button" class="btn primary diag-copy" data-diag="copy">Copy report</button>
        <button type="button" class="btn" data-diag="share" hidden>Share</button>
        <button type="button" class="btn" data-diag="refresh">Refresh</button>
        <button type="button" class="btn" data-diag="close">Close</button>
      </div>
      <p class="diag-status" role="status"></p>
      <div class="diag-body"></div>
    `;
    this.root = root;
    this.body = root.querySelector(".diag-body");
    this.status = root.querySelector(".diag-status");
    const button = (name: string): HTMLButtonElement => root.querySelector(`[data-diag="${name}"]`) as HTMLButtonElement;
    button("copy").addEventListener("click", () => void this.copy());
    button("refresh").addEventListener("click", () => this.refresh());
    button("close").addEventListener("click", () => this.close());
    if (typeof navigator.share === "function") {
      button("share").hidden = false;
      button("share").addEventListener("click", () => void this.share());
    }
    // Capture phase on window: Escape closes this sheet, not the walk behind it (src/main.ts).
    addEventListener("keydown", this.onKey, true);
    document.body.append(root);
    diagMark("diagnostics", "opened");
    this.refresh();
    button("close").focus();
  }

  close(): void {
    if (!this.root) return;
    this.run++; // late results of a running collection are dropped
    removeEventListener("keydown", this.onKey, true);
    this.root.remove();
    this.root = null;
    this.body = null;
    this.status = null;
    if (this.lastFocus instanceof HTMLElement) this.lastFocus.focus();
    this.lastFocus = null;
  }

  private readonly onKey = (event: KeyboardEvent): void => {
    if (event.key !== "Escape") return;
    event.stopImmediatePropagation();
    event.preventDefault();
    this.close();
  };

  private refresh(): void {
    const body = this.body;
    if (!body) return;
    const run = ++this.run;
    this.sections = SECTION_TITLES.map(() => null);
    body.replaceChildren();
    SECTION_TITLES.forEach((title, index) => {
      const section = document.createElement("section");
      section.dataset.index = String(index);
      section.innerHTML = `<h3></h3><pre>collecting…</pre>`;
      (section.querySelector("h3") as HTMLElement).textContent = `${index + 1}. ${title}`;
      body.append(section);
    });
    this.setStatus("Collecting…");
    void collectSections((index, section) => {
      if (run !== this.run || !this.body) return;
      this.sections[index] = section;
      const el = this.body.querySelector(`section[data-index="${index}"]`);
      if (!el) return;
      el.classList.toggle("failed", Boolean(section.error));
      (el.querySelector("pre") as HTMLElement).textContent = (section.error ? [`!! this section failed: ${section.error}`] : []).concat(section.lines).join("\n");
    }).then(() => {
      if (run === this.run) this.setStatus("Ready. Copy report, then paste it to the developer.");
    });
  }

  /** The text as it is now; sections still collecting say so. */
  reportText(): string {
    const sections = SECTION_TITLES.map((title, index) => this.sections[index] ?? { title, lines: ["(still collecting when the report was copied)"] });
    return formatReport({ generatedAt: Date.now(), version: __APP_VERSION__ }, sections);
  }

  private setStatus(text: string): void {
    if (this.status) this.status.textContent = text;
  }

  private async copy(): Promise<void> {
    const text = this.reportText();
    if (await copyText(text)) {
      this.setStatus(`Copied ${text.length} characters. Paste them into your message to the developer.`);
    } else {
      this.showManualCopy(text);
    }
  }

  private async share(): Promise<void> {
    try {
      await navigator.share({ title: "Walk Player diagnostics", text: this.reportText() });
      this.setStatus("Shared.");
    } catch (err) {
      if ((err as Error).name !== "AbortError") this.setStatus(`Could not share: ${(err as Error).message}`);
    }
  }

  /** Neither clipboard path worked: show the text selected, for a manual long-press > Copy. */
  private showManualCopy(text: string): void {
    if (!this.body) return;
    this.body.querySelector(".diag-manual")?.remove();
    const area = document.createElement("textarea");
    area.className = "diag-manual";
    area.readOnly = true;
    area.value = text;
    this.body.prepend(area);
    area.focus();
    area.select();
    this.setStatus("Could not copy automatically. The report is selected below: long-press it and choose Copy.");
  }
}
